import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MobileLayout from '../MobileLayout';
import { apiGet } from '../api';
import { MobileProject } from '../types';

const MobileCustomerDetail: React.FC = () => {
  const { customerId = '' } = useParams<{ customerId: string }>();
  const [projects, setProjects] = useState<MobileProject[]>([]);
  const [customerName, setCustomerName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    Promise.all([
      apiGet<any>(`/customers/${customerId}`),
      apiGet<any[]>(`/projects?customerId=${encodeURIComponent(customerId)}`),
    ])
      .then(([customer, list]) => {
        if (cancel) return;
        // Lead with business name; fall back to contact if no company is set.
        setCustomerName(customer?.company || customer?.name || 'Customer');
        setProjects((list || []).map((p) => ({
          id: p.id,
          title: p.title || p.name || 'Untitled folder',
          customerId: p.customerId,
          status: p.status,
          type: p.type,
          updatedAt: p.updatedAt,
        })));
      })
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load projects'))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [customerId]);

  return (
    <MobileLayout title={customerName || 'Customer'} back>
      <div className="px-3 pt-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Project folders</h2>
      </div>

      {loading && <div className="px-3 py-6 text-center text-sm text-slate-500">Loading…</div>}
      {error && <div className="mx-3 my-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200">{error}</div>}

      <ul className="mt-2 divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
        {projects.map((p) => (
          <li key={p.id}>
            <Link
              to={`/m/projects/${p.id}`}
              className="flex items-center justify-between gap-3 px-3 py-3 active:bg-slate-50 dark:active:bg-slate-800"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">{p.title}</div>
                <div className="text-[11px] text-slate-400">
                  {p.status || 'active'}{p.type ? ` · ${p.type.replace(/_/g, ' ')}` : ''}
                </div>
              </div>
              <span className="text-slate-300">›</span>
            </Link>
          </li>
        ))}
        {!loading && projects.length === 0 && (
          <li className="px-3 py-8 text-center text-sm text-slate-500">No project folders yet for this customer.</li>
        )}
      </ul>
    </MobileLayout>
  );
};

export default MobileCustomerDetail;
